import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    ensureObsidianDashboardFonts,
} from '../../../constants/obsidianDashboardTheme';
import { influencerProfilePhotoImgStyle } from '../utils/creatorProfileHelpers';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const SURFACE_LOW = '#131313';
const SURFACE = '#1a1919';
const SURFACE_HI = '#201f1f';

export interface CreatorProfileHeroIdentityProps {
    name: string;
    handle: string;
    verified: boolean;
    avatarUrl?: string;
    subtitle?: string;
    onEdit?: () => void;
}

export function CreatorProfileHeroIdentity({
    name,
    handle,
    verified,
    avatarUrl,
    subtitle,
    onEdit,
}: CreatorProfileHeroIdentityProps) {
    const profileUrl = `${window.location.origin}/creator/${handle.replace(/^@/, '')}`;

    ensureObsidianDashboardFonts();

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.5, sm: 3 },
                borderRadius: 1.5,
                bgcolor: SURFACE_LOW,
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'flex-start', lg: 'center' },
                justifyContent: 'space-between',
                gap: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    right: -80,
                    top: -80,
                    width: 280,
                    height: 280,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,201,101,0.05)',
                    filter: 'blur(48px)',
                    pointerEvents: 'none',
                }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0, zIndex: 1 }}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            bgcolor: '#262626',
                            boxShadow: `0 0 0 2px rgba(255,201,101,0.4)`,
                        }}>
                        {avatarUrl ? (
                            <Box component="img" src={avatarUrl} alt="" sx={influencerProfilePhotoImgStyle} />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 800,
                                    color: GOLD,
                                    fontSize: 28,
                                }}>
                                {name.slice(0, 1).toUpperCase()}
                            </Box>
                        )}
                    </Box>
                    {verified ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -4,
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <VerifiedIcon sx={{ fontSize: 16, color: MINT }} aria-label="Verified" />
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: { xs: 22, sm: 24 },
                                letterSpacing: '-0.02em',
                                color: '#fff',
                                lineHeight: 1.15,
                            }}>
                            {name}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 500,
                                fontSize: 12,
                                color: ON_VARIANT,
                            }}>
                            {handle}
                        </Typography>
                    </Box>
                    {subtitle ? (
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                color: ON_VARIANT,
                                mt: 0.75,
                                lineHeight: 1.45,
                            }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexShrink: 0,
                    zIndex: 1,
                    alignSelf: { xs: 'stretch', lg: 'auto' },
                    justifyContent: 'flex-end',
                }}>
                {onEdit ? (
                    <Button
                        onClick={onEdit}
                        startIcon={<TuneIcon sx={{ fontSize: 16, color: GOLD }} />}
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 1.5,
                            bgcolor: SURFACE,
                            color: '#fff',
                            textTransform: 'none',
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 600,
                            fontSize: 12,
                            '&:hover': { bgcolor: SURFACE_HI },
                        }}>
                        Settings & Rate Card
                    </Button>
                ) : null}
                <Tooltip title="Public profile">
                    <IconButton
                        component="a"
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Public profile preview"
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: SURFACE,
                            color: ON_VARIANT,
                            '&:hover': { bgcolor: SURFACE_HI, color: '#fff' },
                        }}>
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}
